import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  ChildQuestionTemplate,
  CONNECTOR_OPTIONS,
  DEFAULT_BADGE_COLOR,
  FormItem,
  FormMetadata,
  FormQuestionItem,
  FormTemplate,
  isPageBreak,
  MISSING_SLOT_LABELS,
  PAGE_BREAK_QUESTION_TYPE,
  PageBreak,
  QUESTION_TYPE_MAP_TRANSLATION,
  Question,
  StringsEntry,
  StringsFile,
} from '../../types';
import './EditFormTemplate.css';
import Select from 'react-select';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import EditQuestionModal from './EditQuestionModal';
import QuestionTypePopup from './QuestionTypePopup';
import ImportChildQuestionsDialog from './ImportChildQuestionsDialog';
import { v4 as uuidv4 } from 'uuid';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import SortableQuestionBox from './SortableQuestionBox';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { FaPlus } from 'react-icons/fa';
import { ALL_FORMS_PATH } from '../../consts/consts';
import { useNavigate } from 'react-router-dom';
import {
  getFormsMetadata,
  MissingTranslation,
  SaveConflictError,
  SaveValidationError,
  updateFormTemplate,
} from '../../api/AuthorizationApi';
import { DEFAULT_LANG, useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../consts/translations';
import { computeChildChanges } from '../../utils/childQuestionUtils';
import {
  languageFromCode,
  normalizeFormLanguageCodes,
  PREVIEW_LANGUAGE_OPTIONS,
} from '../../utils/languageMapping';
import { childStringsForLang, missingSlots, questionInLang } from '../../utils/templateLoader';

const TRANSLATION_LANGS = PREVIEW_LANGUAGE_OPTIONS.map(({ code }) => code).filter(
  (code) => code !== DEFAULT_LANG,
);

function missingTranslationLabel(item: MissingTranslation): string {
  const name = item.question_key || item.guid;
  const slot = MISSING_SLOT_LABELS[item.slot] ?? item.slot;
  const option = item.option_guid ? ` (${item.option_guid})` : '';
  return `${name} — ${languageFromCode(item.lang)} — ${slot}${option}`;
}

type EditFormTemplateProps = {
  formTemplate: FormTemplate;
  setFormTemplate: (formTemplate: FormTemplate) => void;
  /** Non-Hebrew strings files; a 404 is {} and a failed fetch is listed in failedLangs instead. */
  stringsByLang: Record<string, StringsFile>;
  setStringsByLang: React.Dispatch<React.SetStateAction<Record<string, StringsFile>>>;
  /** Strings as fetched; the server rejects the save if S3 no longer matches them. */
  loadedStringsByLang: Record<string, StringsFile>;
  failedLangs: string[];
  logout: () => void;
  setFormsMetadata: (data: FormMetadata[]) => void;
  createForm: boolean;
  allQuestions: Question[];
  formLanguages: string[];
};

const EditFormTemplate: React.FC<EditFormTemplateProps> = ({
  formTemplate,
  setFormTemplate,
  stringsByLang,
  setStringsByLang,
  loadedStringsByLang,
  failedLangs,
  logout,
  setFormsMetadata,
  createForm,
  allQuestions,
  formLanguages,
}) => {
  const questions = formTemplate.questions.length > 0 ? formTemplate.questions : [];
  const declaredLangs = normalizeFormLanguageCodes(formLanguages);
  // A failed strings fetch must never be edited or saved as empty, or S3's real file is lost.
  const isMultiLang =
    !createForm &&
    failedLangs.length === 0 &&
    PREVIEW_LANGUAGE_OPTIONS.every(({ code }) => declaredLangs.includes(code));
  const [questionToDelete, setQuestionToDelete] = useState<FormItem | null>(null);
  const [questionToEdit, setQuestionToEdit] = useState<Question | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<{
    question: Question;
    position: number;
    /** Duplicates start from the source question's translations. */
    sourceGuid?: string;
  } | null>(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [showQuestionTypePopup, setShowQuestionTypePopup] = useState<{
    show: boolean;
    position: number | null;
  }>({ show: false, position: null });
  const [importChildDialog, setImportChildDialog] = useState<{
    question: Question;
    childQuestions: Question[];
    position: number;
  } | null>(null);
  const [undoSnapshot, setUndoSnapshot] = useState<{
    questions: FormItem[];
    stringsByLang: Record<string, StringsFile>;
    questionKey: string;
    childCount: number;
  } | null>(null);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(0);
  const [missingTranslations, setMissingTranslations] = useState<MissingTranslation[] | null>(null);
  const [conflictLangs, setConflictLangs] = useState<string[] | null>(null);
  const saveInFlight = useRef(false);

  const UNDO_SECONDS = 15;
  const RING_RADIUS = 30;
  const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

  useEffect(() => {
    if (!undoSnapshot) return;
    setUndoSecondsLeft(UNDO_SECONDS);
    const interval = setInterval(() => setUndoSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    const timeout = setTimeout(() => setUndoSnapshot(null), UNDO_SECONDS * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [undoSnapshot]);

  const addButtonRefs = useRef<{
    [key: number]: React.RefObject<HTMLButtonElement>;
  }>({});
  const sensors = useSensors(useSensor(PointerSensor));
  const questionIds = questions.map((q) => q.guid);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const translation = getTranslation(language);

  /** Split questions by page_break; each page = non–page-break questions in that segment. */
  const getPages = (qs: FormItem[]) => {
    const pages: FormQuestionItem[][] = [];
    let current: FormQuestionItem[] = [];
    for (const q of qs) {
      if (isPageBreak(q)) {
        pages.push(current);
        current = [];
      } else {
        current.push(q);
      }
    }
    pages.push(current);
    return pages;
  };

  // Blank child-template text is yellow: the children carry their own translations.
  const missingLanguagesByGuid = useMemo(() => {
    const result: Record<string, { red: string[]; soft: string[] }> = {};
    if (!isMultiLang) return result;
    for (const q of formTemplate.questions) {
      if (isPageBreak(q)) continue;
      const red: string[] = [];
      const soft: string[] = [];
      for (const lang of TRANSLATION_LANGS) {
        const slots = missingSlots(q, questionInLang(q, stringsByLang[lang]?.[q.guid]));
        if (slots.some((slot) => !slot.childTemplateId)) red.push(languageFromCode(lang));
        else if (slots.length > 0) soft.push(languageFromCode(lang));
      }
      if (red.length > 0 || soft.length > 0) {
        result[q.guid] = { red, soft };
      }
    }
    return result;
  }, [formTemplate.questions, isMultiLang, stringsByLang]);

  const hasEmptyPage = (qs: FormItem[]) => getPages(qs).some((page) => page.length === 0);
  const realQuestionsCount = questions.filter((q) => !isPageBreak(q)).length;

  const renderInputForType = (question: FormItem) => {
    if (isPageBreak(question)) {
      return (
        <span className='page-break-label'>
          {QUESTION_TYPE_MAP_TRANSLATION[PAGE_BREAK_QUESTION_TYPE]}
        </span>
      );
    }
    switch (question.question_type) {
      case 'header':
        return <div className='header-placeholder'>כותרת / תמונה</div>;

      case 'checkbox':
      case 'radio':
        return (
          <div className='option-list'>
            {question.options?.map((option) => (
              <label key={option.guid} className='option-item'>
                <input
                  type={question.question_type}
                  name={question.question_type === 'radio' ? 'radio-group' : undefined}
                />
                <span>{option.text}</span>
              </label>
            ))}
          </div>
        );

      case 'text': {
        const type = question.field_type ?? 'text';
        const hasUnit =
          type === 'number' &&
          question.measurement_unit != null &&
          String(question.measurement_unit).trim() !== '';

        switch (type) {
          case 'number':
            return hasUnit ? (
              <div className='question-input-with-unit' dir='rtl'>
                <input type='number' className='input-field question-input-inside-unit' readOnly />
                <span className='question-input-unit' aria-hidden='true'>
                  {question.measurement_unit}
                </span>
              </div>
            ) : (
              <input type='number' className='input-field' />
            );
          case 'date':
            return <input type='date' className='input-field' />;
          case 'textarea':
            return <textarea className='input-field' rows={4} />;
          default:
            return <input type='text' className='input-field' />;
        }
      }

      case 'select': {
        const selectOptions =
          question.options?.map((opt) => ({
            value: opt.guid,
            label: opt.text,
          })) ?? [];

        return (
          <Select
            options={selectOptions}
            isMulti={question.multiple_select}
            closeMenuOnSelect={!question.multiple_select}
            placeholder='בחר'
            className='select-field'
            hideSelectedOptions={false}
            value={
              question.multiple_select ? selectOptions.slice(0, 1) : (selectOptions[0] ?? null)
            }
          />
        );
      }

      case 'scale': {
        const numOptions = question.scale_num_options ?? 5;
        const minLabel = question.scale_min_text ?? '';
        const maxLabel = question.scale_max_text ?? '';

        const scaleValues = Array.from({ length: numOptions }, (_, i) => i + 1);

        return (
          <div className='scale-container' dir='rtl'>
            <div className='scale-options'>
              {scaleValues.map((value, idx) => (
                <div key={value} className='scale-option'>
                  <input type='radio' name='scale' disabled />
                  <span className='scale-value'>{value}</span>
                  {idx === 0 && <span className='scale-text'>{minLabel}</span>}
                  {idx === numOptions - 1 && <span className='scale-text'>{maxLabel}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      }
      default: {
        const unknownQuestion = question as FormQuestionItem;
        return (
          <input
            type='text'
            className='input-field'
            placeholder={
              unknownQuestion.question_type
                ? `Unsupported type: ${unknownQuestion.question_type}`
                : QUESTION_TYPE_MAP_TRANSLATION[PAGE_BREAK_QUESTION_TYPE]
            }
          />
        );
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = questions.findIndex((q) => q.guid === active.id);
    const newIndex = questions.findIndex((q) => q.guid === over.id);
    const newQuestions = arrayMove(questions, oldIndex, newIndex);
    newQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });
    setFormTemplate({
      ...formTemplate,
      questions: newQuestions,
    });
  };

  const createChildQuestions = (
    parentQuestion: Question,
    allQuestions: Question[],
    existingChildKeys?: Map<string, { key: string; wasCustomized: boolean }>,
    onlySlots?: Set<string>,
  ): Question[] => {
    if (
      !parentQuestion.auto_create_child_questions ||
      !parentQuestion.child_question_templates ||
      parentQuestion.child_question_templates.length === 0
    ) {
      return [];
    }

    const childQuestions: Question[] = [];

    // Iterate over all templates
    parentQuestion.child_question_templates.forEach((template: ChildQuestionTemplate) => {
      if (!template.option_guids || template.option_guids.length === 0) return;

      const shortTemplateId = template.id.substring(0, 6);

      template.option_guids.forEach((optionGuid, optionIndex) => {
        // When a slot filter is provided, skip slots not in the set
        if (onlySlots && !onlySlots.has(`${template.id}_${optionGuid}`)) return;

        const selectedOption = parentQuestion.options?.find((opt) => opt.guid === optionGuid);
        if (!selectedOption) return;

        const optionText = String(selectedOption.text);
        const shortOptionGuid = optionGuid.substring(0, 6);

        // Generate child question key
        // If key_prefix is provided, use shorter format: parentKey_prefix_index
        // Otherwise, use default long format for uniqueness: parentKey_child_templateId_index_optionGuid
        let defaultChildQuestionKey: string;
        if (template.key_prefix && template.key_prefix.trim()) {
          const prefix = template.key_prefix.trim();
          defaultChildQuestionKey = parentQuestion.question_key
            ? `${parentQuestion.question_key}_${prefix}_${optionIndex + 1}`
            : `${prefix}_${optionIndex + 1}`;
        } else {
          defaultChildQuestionKey = parentQuestion.question_key
            ? `${parentQuestion.question_key}_child_${shortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`
            : `child_${shortTemplateId}_${optionIndex + 1}_${shortOptionGuid}`;
        }

        // Use existing custom key if it was customized, otherwise use the default
        // Key in map is templateId_optionGuid to track per-template keys
        const mapKey = `${template.id}_${optionGuid}`;
        const existingKeyInfo = existingChildKeys?.get(mapKey);
        const childQuestionKey = existingKeyInfo?.wasCustomized
          ? existingKeyInfo.key
          : defaultChildQuestionKey;

        // Check if child question key already exists (only for default keys)
        if (!existingKeyInfo?.wasCustomized) {
          const existingChildKey = allQuestions.find(
            (q) =>
              q.question_key?.toLowerCase() === childQuestionKey.toLowerCase() &&
              q.guid !== parentQuestion.guid,
          );

          if (existingChildKey) {
            // Skip creating duplicate
            return;
          }
        }

        // Create child question text using template: text_before + answer + text_after
        // No automatic spacing - user controls spacing in their text
        const textBefore = template.text_before || '';
        const textAfter = template.text_after || '';
        const childQuestionText = `${textBefore}${optionText}${textAfter}`;

        // Create child question based on template
        const templateDescription = template.description?.trim() || '';
        const childQuestion: Question = {
          question_index: 0, // Will be set later
          text: childQuestionText,
          guid: uuidv4(),
          show_description: template.show_description === true && templateDescription.length > 0,
          mandatory: false,
          description: templateDescription,
          question_key: childQuestionKey,
          show_question: true,
          question_type: template.question_type || 'radio',
          // For text questions, don't set options. For other types, use shared options from template
          options:
            template.question_type === 'text' || !template.options
              ? []
              : template.options.map((opt) => ({
                  ...opt,
                  guid: uuidv4(), // Generate new GUIDs for child question options
                })),
          appeared_conditions: [
            {
              connected_question_guid: parentQuestion.guid,
              operator: 'contains',
              values: [optionGuid],
              connector: CONNECTOR_OPTIONS[0].value,
            },
          ],
          field_type: template.field_type,
          measurement_unit: template.measurement_unit || undefined,
          auto_generated_from_parent: parentQuestion.guid, // Mark as auto-generated child
          auto_generated_from_template: template.id, // Track which template created this
          auto_generated_badge_color: template.badge_color || DEFAULT_BADGE_COLOR, // Badge color from template
        };

        childQuestions.push(childQuestion);
      });
    });

    return childQuestions;
  };

  const handleEditQuestion = (
    updatedQuestion: Question,
    entriesByLang?: Record<string, StringsEntry>,
  ) => {
    let updatedQuestions: FormItem[];
    let parentIndex: number;

    if (pendingQuestion && pendingQuestion.question.guid === updatedQuestion.guid) {
      updatedQuestions = [...questions];
      updatedQuestions.splice(pendingQuestion.position, 0, updatedQuestion as FormQuestionItem);
      parentIndex = pendingQuestion.position;
      setPendingQuestion(null);
    } else {
      updatedQuestions = formTemplate.questions.map((q, idx) =>
        q.guid === updatedQuestion.guid
          ? ({ ...updatedQuestion, question_index: idx } as FormQuestionItem)
          : { ...q, question_index: idx },
      );
      parentIndex = updatedQuestions.findIndex((q) => q.guid === updatedQuestion.guid);
    }

    const parentGuid = updatedQuestion.guid;

    if (
      updatedQuestion.auto_create_child_questions &&
      updatedQuestion.child_question_templates?.length
    ) {
      const originalQuestion = formTemplate.questions.find(
        (q) => q.guid === parentGuid && !isPageBreak(q),
      ) as Question | undefined;

      const existingChildren = updatedQuestions.filter(
        (q) => !isPageBreak(q) && (q as FormQuestionItem).auto_generated_from_parent === parentGuid,
      ) as FormQuestionItem[];

      // First-time creation (no prior children) — create all slots, no diffing needed
      if (!originalQuestion || existingChildren.length === 0) {
        parentIndex = updatedQuestions.findIndex((q) => q.guid === parentGuid);
        const newChildren = createChildQuestions(
          updatedQuestion,
          updatedQuestions.filter((q): q is FormQuestionItem => !isPageBreak(q)),
        );
        newChildren.forEach((child, idx) => {
          updatedQuestions.splice(parentIndex + 1 + idx, 0, child as FormQuestionItem);
        });
      } else {
        const changes = computeChildChanges(originalQuestion, updatedQuestion, existingChildren);

        const hasDestructive = changes.some(
          (c) => c.actions.includes('recreate') || c.actions.includes('remove'),
        );

        if (hasDestructive) {
          setUndoSnapshot({
            questions: formTemplate.questions.map((q) =>
              isPageBreak(q) ? { ...q } : (deepCopyQuestion(q as Question) as FormItem),
            ),
            stringsByLang,
            questionKey: updatedQuestion.question_key ?? updatedQuestion.guid,
            childCount: changes.filter(
              (c) => c.actions.includes('recreate') || c.actions.includes('remove'),
            ).length,
          });
        }

        // 1. Remove children that need recreate or remove
        const guidsToRemove = new Set(
          changes
            .filter((c) => c.actions.includes('recreate') || c.actions.includes('remove'))
            .map((c) => c.existingChild?.guid)
            .filter(Boolean),
        );
        updatedQuestions = updatedQuestions.filter((q) => !guidsToRemove.has(q.guid));

        // 2. Apply in-place patches to kept children
        updatedQuestions = updatedQuestions.map((q) => {
          if (isPageBreak(q)) return q;
          const change = changes.find((c) => c.existingChild?.guid === q.guid);
          if (!change) return q;
          let patched = { ...q } as FormQuestionItem;
          if (change.actions.includes('update-text') && change.newText !== undefined)
            patched = { ...patched, text: change.newText };
          if (change.actions.includes('update-key') && change.newKey !== undefined)
            patched = { ...patched, question_key: change.newKey };
          if (change.actions.includes('update-meta') && change.metaUpdates)
            patched = { ...patched, ...change.metaUpdates };
          return patched;
        });

        // 3. Collect kept children (after patching), remove from their current positions
        const keptChildren = updatedQuestions.filter(
          (q) =>
            !isPageBreak(q) && (q as FormQuestionItem).auto_generated_from_parent === parentGuid,
        ) as FormQuestionItem[];
        const keptGuids = new Set(keptChildren.map((c) => c.guid));
        updatedQuestions = updatedQuestions.filter((q) => !keptGuids.has(q.guid));

        // 4. Create new children for create/recreate slots
        const slotsToCreate = new Set(
          changes
            .filter((c) => c.actions.includes('create') || c.actions.includes('recreate'))
            .map((c) => `${c.templateId}_${c.optionGuid}`),
        );
        const newChildren =
          slotsToCreate.size > 0
            ? createChildQuestions(
                updatedQuestion,
                updatedQuestions.filter((q): q is FormQuestionItem => !isPageBreak(q)),
                undefined,
                slotsToCreate,
              )
            : [];

        // 5. Merge kept + new in canonical template × optionGuid order
        const orderedChildren: FormQuestionItem[] = [];
        for (const template of updatedQuestion.child_question_templates!) {
          for (const optGuid of template.option_guids ?? []) {
            const kept = keptChildren.find(
              (c) =>
                c.auto_generated_from_template === template.id &&
                c.appeared_conditions?.some(
                  (cond) =>
                    cond.connected_question_guid === parentGuid && cond.values?.includes(optGuid),
                ),
            );
            const created = newChildren.find(
              (c) =>
                (c as FormQuestionItem).auto_generated_from_template === template.id &&
                (c as FormQuestionItem).appeared_conditions?.some((cond) =>
                  cond.values?.includes(optGuid),
                ),
            );
            if (kept) orderedChildren.push(kept);
            else if (created) orderedChildren.push(created as FormQuestionItem);
          }
        }

        // 6. Re-insert ordered children right after parent
        parentIndex = updatedQuestions.findIndex((q) => q.guid === parentGuid);
        orderedChildren.forEach((child, idx) => {
          updatedQuestions.splice(parentIndex + 1 + idx, 0, child);
        });

        if (!hasDestructive) setUndoSnapshot(null);
      }
    } else {
      // Auto-create off (toggled off or parent type changed to non-options) — remove all auto-generated children
      const childrenToRemove = updatedQuestions.filter(
        (q) => !isPageBreak(q) && (q as FormQuestionItem).auto_generated_from_parent === parentGuid,
      );
      if (childrenToRemove.length > 0) {
        setUndoSnapshot({
          questions: formTemplate.questions.map((q) =>
            isPageBreak(q) ? { ...q } : (deepCopyQuestion(q as Question) as FormItem),
          ),
          stringsByLang,
          questionKey: updatedQuestion.question_key ?? updatedQuestion.guid,
          childCount: childrenToRemove.length,
        });
      }
      updatedQuestions = updatedQuestions.filter((q) => {
        if (q.guid === parentGuid) return true;
        if (isPageBreak(q)) return true;
        return (q as FormQuestionItem).auto_generated_from_parent !== parentGuid;
      });
    }

    updatedQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });
    setFormTemplate({ ...formTemplate, questions: updatedQuestions });

    if (entriesByLang) {
      const isChildOfParent = (q: FormItem) =>
        !isPageBreak(q) && q.auto_generated_from_parent === parentGuid;
      const previousChildGuids = questions.filter(isChildOfParent).map((q) => q.guid);
      const children = updatedQuestions.filter(isChildOfParent) as Question[];
      setStringsByLang((prev) => {
        const next = { ...prev };
        for (const [lang, entry] of Object.entries(entriesByLang)) {
          const langStrings = { ...(prev[lang] ?? {}), [parentGuid]: entry };
          previousChildGuids.forEach((guid) => delete langStrings[guid]);
          const langParent = questionInLang(updatedQuestion, entry);
          next[lang] = {
            ...langStrings,
            ...childStringsForLang(prev[lang] ?? {}, langParent, children),
          };
        }
        return next;
      });
    }
  };

  const getButtonRef = (position: number) => {
    if (!addButtonRefs.current[position]) {
      addButtonRefs.current[position] = { current: null };
    }
    return addButtonRefs.current[position];
  };

  const AddQuestionButton: React.FC<{
    onClick: () => void;
    position: number;
  }> = ({ onClick, position }) => {
    const buttonRef = getButtonRef(position);

    return (
      <div className='add-question-btn-wrapper'>
        <button
          ref={buttonRef}
          className='inline-add-question-btn'
          onClick={onClick}
          title='הוסף שאלה'
        >
          <FaPlus className='add-question-icon' />
          <span>הוסף שאלה</span>
        </button>
        {showQuestionTypePopup.show && showQuestionTypePopup.position === position && (
          <QuestionTypePopup
            allQuestions={allQuestions}
            currentFormQuestions={questions}
            onNewQuestion={handleNewQuestion}
            onExistingQuestion={handleExistingQuestion}
            onAddPageBreak={handleAddPageBreak}
            onClose={() => setShowQuestionTypePopup({ show: false, position: null })}
            buttonRef={buttonRef}
            position={position}
          />
        )}
      </div>
    );
  };

  const handleAddQuestion = (position: number) => {
    setShowQuestionTypePopup({ show: true, position });
  };

  const handleAddPageBreak = (position: number) => {
    const pageBreakItem: PageBreak = {
      guid: uuidv4(),
      question_index: position,
      question_type: PAGE_BREAK_QUESTION_TYPE,
      show_question: true,
      options: [],
    };
    const updatedQuestions = [...questions];
    updatedQuestions.splice(position, 0, pageBreakItem);
    updatedQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });
    setFormTemplate({ ...formTemplate, questions: updatedQuestions });
    setShowQuestionTypePopup({ show: false, position: null });
  };

  const handleNewQuestion = () => {
    const position = showQuestionTypePopup.position!;
    const newQuestion = {
      question_index: position,
      text: '',
      guid: uuidv4(),
      show_description: false,
      mandatory: true,
      description: '',
      show_question: true,
      options: [],
      question_type: 'header',
    };

    setPendingQuestion({ question: newQuestion, position });
    setQuestionToEdit(newQuestion);
    setShowQuestionTypePopup({ show: false, position: null });
  };

  const deepCopyQuestion = (question: Question): Question => {
    return {
      ...question,
      options: question.options ? question.options.map((opt) => ({ ...opt })) : [],
      appeared_conditions: question.appeared_conditions
        ? question.appeared_conditions.map((cond) => ({
            ...cond,
            values: [...cond.values],
          }))
        : undefined,
      question_conditions: question.question_conditions
        ? question.question_conditions.map((cond) => ({
            ...cond,
            values: [...cond.values],
          }))
        : undefined,
    };
  };

  const handleExistingQuestion = (selectedQuestion: Question, childQuestions: Question[]) => {
    const position = showQuestionTypePopup.position!;
    setShowQuestionTypePopup({ show: false, position: null });

    // If there are child questions, show dialog to ask user
    if (childQuestions.length > 0) {
      setImportChildDialog({
        question: selectedQuestion,
        childQuestions,
        position,
      });
      return;
    }

    // No children - import directly (clear templates to prevent auto-regeneration)
    importQuestionWithoutChildren(selectedQuestion, position);
  };

  const importQuestionWithoutChildren = (selectedQuestion: Question, position: number) => {
    const questionCopy = {
      ...deepCopyQuestion(selectedQuestion),
      guid: uuidv4(),
      question_index: position,
      // Clear child templates to prevent auto-regeneration
      auto_create_child_questions: false,
      child_question_templates: undefined,
    };

    const updatedQuestions = [...questions];
    updatedQuestions.splice(position, 0, questionCopy as FormQuestionItem);
    updatedQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });

    setFormTemplate({
      ...formTemplate,
      questions: updatedQuestions,
    });
  };

  const importQuestionWithChildren = (
    selectedQuestion: Question,
    childQuestions: Question[],
    position: number,
  ) => {
    // Create GUID mapping: oldGuid -> newGuid
    const guidMapping = new Map<string, string>();

    // Create new GUID for parent
    const newParentGuid = uuidv4();
    guidMapping.set(selectedQuestion.guid, newParentGuid);

    // Create new GUIDs for all children
    childQuestions.forEach((child) => {
      guidMapping.set(child.guid, uuidv4());
    });

    // Copy parent with new GUID (keep templates since we're importing children)
    const parentCopy = {
      ...deepCopyQuestion(selectedQuestion),
      guid: newParentGuid,
      question_index: position,
    };

    // Copy children with new GUIDs, updated parent references, but KEEP original question_key
    const childCopies = childQuestions.map((child, idx) => {
      const newChildGuid = guidMapping.get(child.guid)!;
      const newParentRef = child.auto_generated_from_parent
        ? guidMapping.get(child.auto_generated_from_parent)
        : undefined;

      // Deep copy and update references
      const childCopy = {
        ...deepCopyQuestion(child),
        guid: newChildGuid,
        question_index: position + 1 + idx,
        auto_generated_from_parent: newParentRef,
        // question_key stays the same - this is the key feature!
      };

      // Update appeared_conditions to reference new parent GUID
      if (childCopy.appeared_conditions) {
        childCopy.appeared_conditions = childCopy.appeared_conditions.map((cond) => ({
          ...cond,
          connected_question_guid:
            guidMapping.get(cond.connected_question_guid) || cond.connected_question_guid,
          values: [...cond.values],
        }));
      }

      return childCopy;
    });

    // Insert all questions
    const updatedQuestions = [...questions];
    updatedQuestions.splice(
      position,
      0,
      parentCopy as FormQuestionItem,
      ...(childCopies as FormQuestionItem[]),
    );
    updatedQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });

    setFormTemplate({
      ...formTemplate,
      questions: updatedQuestions,
    });
  };

  function handleDeleteQuestion(question: FormItem) {
    const parentGuid = question.guid;
    const updatedQuestions = questions.filter((q) => {
      if (q.guid === parentGuid) return false;
      if (isPageBreak(q)) return true;
      if (q.auto_generated_from_parent === parentGuid) return false;
      return true;
    });
    updatedQuestions.forEach((q, idx) => {
      q.question_index = idx;
    });
    setFormTemplate({
      ...formTemplate,
      questions: updatedQuestions,
    });
    if (isMultiLang) {
      const keptGuids = new Set(updatedQuestions.map((q) => q.guid));
      setStringsByLang((prev) =>
        Object.fromEntries(
          Object.entries(prev).map(([lang, langStrings]) => [
            lang,
            Object.fromEntries(Object.entries(langStrings).filter(([guid]) => keptGuids.has(guid))),
          ]),
        ),
      );
    }
  }

  const handleSave = async () => {
    if (saveInFlight.current) {
      return;
    }
    saveInFlight.current = true;
    try {
      setLoadingSave(true);
      const languages = createForm ? [DEFAULT_LANG] : declaredLangs;
      await updateFormTemplate(
        formTemplate,
        translation,
        logout,
        createForm,
        languages,
        isMultiLang ? stringsByLang : undefined,
        isMultiLang ? loadedStringsByLang : undefined,
      );
      setMissingTranslations(null);
      navigate(`${ALL_FORMS_PATH}`);
    } catch (error) {
      if (error instanceof SaveValidationError) {
        setMissingTranslations(error.missingTranslations);
        return;
      }
      if (error instanceof SaveConflictError) {
        setConflictLangs(error.conflictLangs);
        return;
      }
      console.error('Error saving form template:', error);
    } finally {
      saveInFlight.current = false;
      setLoadingSave(false);
    }
  };

  const handleCancel = async () => {
    const response = await getFormsMetadata(translation, logout);
    setFormsMetadata(response.data as FormMetadata[]);
    navigate(`${ALL_FORMS_PATH}`);
  };

  return (
    <div className='edit-form-template'>
      <div className='edit-form-template-header-row'>
        <div className='edit-form-template-header-actions'>
          <button
            className='save-btn'
            onClick={() => handleSave()}
            disabled={
              loadingSave ||
              !formTemplate.form_name.trim() ||
              realQuestionsCount === 0 ||
              hasEmptyPage(questions)
            }
          >
            שמור
          </button>
          <button className='cancel-btn' onClick={handleCancel}>
            ביטול
          </button>
        </div>
        {createForm ? (
          <input
            className='edit-form-template-header-input'
            type='text'
            placeholder='הזן שם לטופס'
            value={formTemplate.form_name}
            onChange={(e) => setFormTemplate({ ...formTemplate, form_name: e.target.value })}
            autoFocus
          />
        ) : (
          <h2 className='edit-form-template-header'>שאלון {formTemplate.form_name}</h2>
        )}
      </div>
      {!createForm && failedLangs.length > 0 && (
        <div className='translations-load-warning' role='alert'>
          לא ניתן לטעון תרגומים ל: {failedLangs.map(languageFromCode).join(', ')}. רעננו את הדף כדי
          לערוך תרגומים.
        </div>
      )}
      {missingTranslations && missingTranslations.length > 0 && (
        <div className='missing-translation-overlay' role='dialog' aria-modal='true'>
          <div className='missing-translation-popup'>
            <p>לא ניתן לשמור. חסר תרגום בשאלות האלה.</p>
            <ul>
              {missingTranslations.map((item) => (
                <li
                  key={`${item.guid}-${item.slot}-${item.option_guid ?? ''}-${item.child_template_id ?? ''}-${item.lang}`}
                >
                  {missingTranslationLabel(item)}
                </li>
              ))}
            </ul>
            <div className='missing-translation-popup-actions'>
              <button type='button' onClick={() => setMissingTranslations(null)}>
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      {conflictLangs && (
        <div className='missing-translation-overlay' role='dialog' aria-modal='true'>
          <div className='missing-translation-popup'>
            <p>
              התרגומים ב-{conflictLangs.map(languageFromCode).join(', ')} עודכנו מאז שפתחת את
              הטופס. טען את הטופס מחדש כדי לא לדרוס אותם.
            </p>
            <div className='missing-translation-popup-actions'>
              <button type='button' onClick={() => setConflictLangs(null)}>
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      <div className='form-wrapper'>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
            <AddQuestionButton onClick={() => handleAddQuestion(0)} position={0} />
            {questions.map((question, index) => {
              const pageNumber = 1 + questions.slice(0, index).filter((q) => isPageBreak(q)).length;
              const isPageBreakItem = isPageBreak(question);
              return (
                <React.Fragment key={question.guid}>
                  <SortableQuestionBox
                    question={question}
                    pageNumber={pageNumber}
                    isPageBreak={isPageBreakItem}
                    missingLanguages={missingLanguagesByGuid[question.guid]?.red}
                    softMissingLanguages={missingLanguagesByGuid[question.guid]?.soft}
                    renderInputForType={renderInputForType}
                    onEdit={(q) => {
                      if (!isPageBreak(q)) setQuestionToEdit(q);
                    }}
                    onDelete={setQuestionToDelete}
                    onDuplicate={(q: FormItem) => {
                      const currentIndex = questions.findIndex((item) => item.guid === q.guid);
                      if (isPageBreak(q)) {
                        const newBreak: PageBreak = {
                          guid: uuidv4(),
                          question_index: currentIndex + 1,
                          question_type: PAGE_BREAK_QUESTION_TYPE,
                          show_question: true,
                          options: [],
                        };
                        const updated = [...questions];
                        updated.splice(currentIndex + 1, 0, newBreak);
                        updated.forEach((item, idx) => {
                          item.question_index = idx;
                        });
                        setFormTemplate({
                          ...formTemplate,
                          questions: updated,
                        });
                        return;
                      }
                      const duplicatedQuestion = {
                        ...deepCopyQuestion(q),
                        guid: uuidv4(),
                        question_index: currentIndex + 1,
                      };
                      setPendingQuestion({
                        question: duplicatedQuestion,
                        position: currentIndex + 1,
                        sourceGuid: q.guid,
                      });
                      setQuestionToEdit(duplicatedQuestion);
                    }}
                  />
                  <AddQuestionButton
                    onClick={() => handleAddQuestion(index + 1)}
                    position={index + 1}
                  />
                </React.Fragment>
              );
            })}
          </SortableContext>
        </DndContext>

        {questionToDelete && (
          <DeleteConfirmationModal
            text='האם אתה בטוח שברצונך למחוק את השאלה?'
            onCancel={() => setQuestionToDelete(null)}
            onConfirm={() => {
              handleDeleteQuestion(questionToDelete);
              setQuestionToDelete(null);
            }}
          />
        )}

        {questionToEdit && (
          <EditQuestionModal
            questions={questions}
            questionInEdit={questionToEdit}
            stringsForQuestion={
              isMultiLang
                ? Object.fromEntries(
                    TRANSLATION_LANGS.map((lang) => [
                      lang,
                      stringsByLang[lang]?.[pendingQuestion?.sourceGuid ?? questionToEdit.guid] ??
                        {},
                    ]),
                  )
                : undefined
            }
            onSave={handleEditQuestion}
            onClose={() => {
              setQuestionToEdit(null);
              setPendingQuestion(null);
            }}
          />
        )}

        {importChildDialog && (
          <ImportChildQuestionsDialog
            childCount={importChildDialog.childQuestions.length}
            onImportWithChildren={() => {
              importQuestionWithChildren(
                importChildDialog.question,
                importChildDialog.childQuestions,
                importChildDialog.position,
              );
              setImportChildDialog(null);
            }}
            onImportWithoutChildren={() => {
              importQuestionWithoutChildren(importChildDialog.question, importChildDialog.position);
              setImportChildDialog(null);
            }}
            onCancel={() => setImportChildDialog(null)}
          />
        )}
      </div>

      {undoSnapshot && (
        <div className='undo-toast'>
          <svg
            className='undo-ring'
            width='72'
            height='72'
            viewBox='0 0 72 72'
            role='button'
            aria-label={`בטל שינוי — ${undoSecondsLeft} שניות נותרו`}
            onClick={() => {
              setFormTemplate({
                ...formTemplate,
                questions: undoSnapshot.questions,
              });
              setStringsByLang(undoSnapshot.stringsByLang);
              setUndoSnapshot(null);
            }}
          >
            <circle cx='36' cy='36' r={RING_RADIUS} fill='none' stroke='#334155' strokeWidth='5' />
            <circle
              cx='36'
              cy='36'
              r={RING_RADIUS}
              fill='none'
              stroke='#3b82f6'
              strokeWidth='5'
              strokeLinecap='round'
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - undoSecondsLeft / UNDO_SECONDS)}
              transform='rotate(-90 36 36)'
              className='undo-ring-arc'
            />
            {/* transparent disc so the whole inside is clickable */}
            <circle cx='36' cy='36' r={RING_RADIUS} fill='transparent' />
            <text
              x='36'
              y='41'
              textAnchor='middle'
              fill='#f8fafc'
              fontSize='13'
              fontWeight='bold'
              className='undo-ring-label'
            >
              ביטול
            </text>
            <text
              x='36'
              y='41'
              textAnchor='middle'
              fill='#94a3b8'
              fontSize='15'
              fontWeight='bold'
              className='undo-ring-seconds'
            >
              {undoSecondsLeft}
            </text>
          </svg>
          <span className='undo-toast-msg'>
            שינויים ב־{undoSnapshot.childCount} שאלות ילד
            <span className='undo-toast-key-row'>
              <span className='undo-toast-key-label'>במפתח השאלה:</span>
              <code className='undo-toast-key'>{undoSnapshot.questionKey}</code>
            </span>
          </span>
          <button
            className='undo-toast-dismiss'
            onClick={() => setUndoSnapshot(null)}
            aria-label='סגור'
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default EditFormTemplate;
